// React dependencies
import React, { useEffect, useState } from 'react';

// External dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Grid, TextField, Icon, Typography, Dialog } from '@material-ui/core';
import { toast } from 'react-toastify';
import _ from 'lodash';

// Internal dependencies
import Constants, { IMAGE_FORMATS } from '../../../common/constants';
import Editor from './editor';
import FixedCropper from '../../global/image/FixedCropper';
import { convertDataURLtoBlob } from '../../../common/util';
import { IsEmpty } from '../../../common/checks';
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import { handleError } from '../../../common/helpers';
import SupportIdAlert from '../../common/SupportIdAlert';

const maxFileSize = 2_000_000;

const Post = (props) => {
  const [loadEditor, setLoadEditor] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [postTitle, setPostTitle] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [description, setDescription] = useState('');
  const [plainText, setPlainText] = useState('');
  const [id, setId] = useState('');
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('');
  const [src, setSrc] = useState('');
  const [originalFile, setOriginalFile] = useState('');
  const [currentFile, setCurrentFile] = useState('');
  const [requestId, setRequestId] = useState(null);

  async function fetchPostData() {
    const post = await apiService.getPostById(props.match.params.postId);
    setLoadEditor(false);
    if (!IsEmpty(post)) {
      setLoadEditor(true);
      setId(post.id);
      setPostTitle(post.title ? post.title : '');
      setDescription(post.description ? post.description : '');
      setPostImages(post.images ? post.images : []);
      setIsLoading(false);
    } else {
      props.onRedirect();
    }
  }

  useEffect(() => {
    fetchPostData();
  }, []);

  const onChangeFields = (e) => {
    switch (e.target.name) {
      case 'postTitle':
        setPostTitle(e.target.value);
        break;
      default:
        break;
    }
  };

  const onChangeDescription = (data) => {
    setDescription(data.toString());
  };

  const onChangePlainText = (data) => {
    setPlainText(data);
  };

  const onCheckPass = () => {
    let flag = false;

    if (!IsEmpty(postImages)) {
      for (const i in postImages) {
        if (description.includes(postImages[i].display)) {
          flag = true;
          break;
        }
      }
    }

    return flag;
  };

  const handleUpdate = async (data, toastMsg) => {
    try {
      await apiService.updatePost(props.match.params.postId, data);
      toast.success(toastMsg || 'Post updated');
      setRequestId(null);
    } catch (err) {
      if (!IsEmpty(err?.requestId)) {
        setRequestId(err.requestId);
      } else {
        setRequestId(null);
      }
      handleError(
        err,
        'Error in Post.article.handleUpdate()',
        { additionalData: { postId: props.match.params.postId } },
      );
    } finally {
      setUpdating(false);
    }
  };

  const getImgUrlsFromDesc = (desc) => {
    const parser  = new DOMParser();
    const doc     = parser.parseFromString(desc, 'text/html');
    const imgUrls = [].slice.call(doc.querySelectorAll('img')).map((img) => {
      const url = img.getAttribute('src');
      let dataUrl = false;
      if (!IsEmpty(url)) {
        dataUrl = url;
      }
      return dataUrl;
    });

    const newImgs = [];
    if (!IsEmpty(imgUrls)) {
      _.forEach(imgUrls, (v) => {
        newImgs.push({ display: v, original: v });
      });
    }

    return newImgs;
  };

  const handleUpdatePost = async (e, postStatus) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      setUpdating(true);
      let status;
      if (postStatus === Constants.Status.Live) {
        if (onCheckPass() === true) {
          status = 'Live';
        } else {
          toast.error('Article requires Media to be Live');
          setUpdating(false);
          return;
        }
      } else {
        status = 'Draft';
      }

      // retrieve the image urls from the description.
      const newImgs = getImgUrlsFromDesc(description);

      const payload = {
        title:  postTitle,
        description,
        images: newImgs,
        status,
      };
      await handleUpdate(payload, `Article saved as ${status}.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadImage = (files) => {
    if (files.length > 0) {
      if (!IMAGE_FORMATS.includes(files[0].type)) {
        toast.error('Only JPG, JPEG and PNG files are supported.');
        return;
      }
      if (files[0].size > maxFileSize) {
        toast.error('Maximum file size of 2MB exceeded.');
        return;
      }
      setCurrentFile(files[0]);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSrc(reader.result);
        const originalImageBlob = convertDataURLtoBlob(reader.result);
        setOriginalFile(new File([originalImageBlob], 'o.jpeg', { type: 'image/jpeg' }));
      });
      reader.readAsDataURL(files[0]);
      setOpen(true);
      setType('upload');
    }
  };

  const handleSaveImage = async (file) => {
    try {
      setIsLoading(true);
      if (file) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('image', originalFile);
        formData.append('type', 'images');

        const result = await apiService.uploadPostMedia(props.match.params.postId, formData, file);
        let updatedDesc = '';
        if (result) {
          updatedDesc = description.concat('<p><img src="').concat(result.display).concat('" /></p><p>&nbsp;<p>');
          setDescription(updatedDesc);

          if (IsEmpty(postImages)) {
            setPostImages([result]);
          }

          setUpdating(true);

          let payload = {
            title:       postTitle,
            description: updatedDesc,
          };

          // we need to save initial upload to the db right away so the post can return the updated data with image.
          if (IsEmpty(postImages) && !IsEmpty(result)) {
            payload = { ...payload, images: [result] };
          } else {
            const newImgs = getImgUrlsFromDesc(updatedDesc);
            payload = { ...payload, images: newImgs };
          }

          await handleUpdate(payload, 'Image Uploaded to article.');
          setOpen(false);
        }
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseCropper = () => {
    setOpen(false);
    setSrc('');
    setCurrentFile('');
    setType('');
  };

  if (isLoading && IsEmpty(id)) {
    return <OverLoading />;
  }

  return (
    <div className="container-fluid">
      {isLoading && <OverLoading />}
      <div className="row">
        <div className="col-xs-12 col-lg-2 col-xl-3" />
        <div className="col-xs-12 col-lg-8 col-xl-6 d-flex flex-row">
          <div className="content-header" style={{ backgroundColor: '#fff', borderBottom: 'none' }}>
            <Typography variant="h1" className="d-flex align-items-center">
              <div onClick={props.onRedirect} className="d-flex align-items-center" aria-hidden="true">
                <Icon className="back">arrow_back</Icon>
              </div>
            </Typography>
          </div>
          <h3 className="ml-1">{`Article ID: ${id}`}</h3>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-lg-2 col-xl-3" />
        <div className="col-xs-12 col-lg-8 col-xl-6">
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <h5>Title</h5>
              <TextField
                name="postTitle"
                placeholder="Enter Article Title"
                multiline
                className="d-flex"
                value={postTitle}
                onChange={(e) => onChangeFields(e)}
              />
            </Grid>
            <Grid item>
              <h5>Content</h5>
              {loadEditor && (
                <Editor
                  onChangeDescription={(e) => onChangeDescription(e)}
                  onChangePlainText={onChangePlainText}
                  description={description}
                  handleUploadImage={handleUploadImage}
                  images={postImages}
                />
              )}
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="row mt-5">
        <div className="col-xs-12 col-md-2 col-lg-4" />
        <div className="col-xs-12 col-md-8 col-lg-4 d-flex flex-column">
          <SupportIdAlert requestId={requestId} />
        </div>
      </div>

      <div className={`row ${!IsEmpty(requestId) ? 'mt-3' : 'mt-5'}`}>
        <div className="col-xs-12 col-md-2 col-lg-4" />
        <div className="col-xs-12 col-md-8 col-lg-4">
          <Grid style={{ marginTop: 10 }} item>
            <Button
              fullWidth
              onClick={(e) => { handleUpdatePost(e, 'Draft'); }}
              disabled={!!updating}
              variant="contained"
            >
              Save As Draft
            </Button>
          </Grid>
          <Grid style={{ marginTop: 10 }} item>
            <Button
              fullWidth
              onClick={(e) => { handleUpdatePost(e, 'Live'); }}
              disabled={!!updating}
              variant="contained"
              color="secondary"
            >
              Submit
            </Button>
          </Grid>
        </div>
      </div>

      <Dialog
        fullScreen
        open={open}
        onClose={handleCloseCropper}
      >
        <FixedCropper
          width={4}
          height={3}
          minUploadPixelWidth={400}
          minUploadPixelHeight={300}
          cropPixelWidth={800}
          cropPixelHeight={600}
          outputPixelWidth={800}
          outputPixelHeight={600}
          type={type}
          url={null}
          src={src}
          file={currentFile}
          handleSave={handleSaveImage}
          handleClose={handleCloseCropper}
        />
      </Dialog>
    </div>
  );
};

Post.defaultProps = {
  match: {
    params: { postId: null },
  },
  onRedirect: () => {},
};

Post.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      postId: PropTypes.string.isRequired,
    }),
  }),
  onRedirect: PropTypes.func,
};

export default connect(null, { })(Post);
